import {
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VisuallyHidden,
} from '@chakra-ui/react';
import { mdiChevronDown, mdiChevronUp, mdiMagnify } from '@mdi/js';
import { t } from '@transifex/native';
import { T } from '@transifex/react';
import {
  GroupBase,
  MultiValueProps,
  OptionProps,
  Select as ReactSelect,
  components as ReactSelectComponents,
} from 'chakra-react-select';
import {
  DataTable,
  DataTableSortingRule,
} from 'features/common/components/DataTable';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { useGetApplicationNodes } from 'features/common/tenant/hooks/useGetApplicationNodes';
import { allowedSearchCharactersRegex } from 'features/common/validations/matchers';
import { useGetListSitecoreContexts } from 'features/context-ids/api/useGetListSitecoreContexts';
import { SensitiveResourceIcon } from 'features/context-ids/components/SensitiveResourceIcon';
import { CONTEXT_IDS_TEXT } from 'features/context-ids/constants';
import { useDebounce } from 'features/common/hooks/useDebounce';
import {
  ExtendedSitecoreContextType,
  ListSitecoreContextsResponse,
  SitecoreContextMappingType,
  SitecoreContextType,
} from 'features/context-ids/types/types';
import {
  RESOURCES_CONFIG,
  isDisplayableResource,
  isSearchProductCode,
  isSensitiveResource,
} from 'features/context-ids/utils';
import { getLoadingMessage } from 'features/user-groups/components/AddMembersDropdown/utils';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import EmptyState from '../../../../../common/components/EmptyState';
import { history } from '../../../../../common/history/history';
import { getContextIdsDetailsRoute } from '../../../../routes';
import {
  getEdgeVariantFilterKey,
  getTableOptions,
} from './helpers/contextIdsTableHelper';
import { RegenerateContextIdModal } from './RegenerateContextIdModal';
import { ScopedContextLoaders } from './ScopedContextLoaders';

const NOOP_TABLE_CHANGE = () => undefined;
const EMPTY_CONTEXTS: SitecoreContextType[] = [];
const TABLE_HEADER_ANALYTICS_PROPS = {
  'data-behavior-analytics-id': 'contexts sorting',
};
const PAGE_SIZE = 10;

const LoadMoreButton = ({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) => (
  <Box textAlign='center' pb={6}>
    <Button
      isLoading={isLoading}
      variant='outline'
      aria-label='load more context ids'
      size='sm'
      data-testid='load-more-context-ids-button'
      onClick={onClick}
    >
      Load More
    </Button>
  </Box>
);

const onClickRow = (
  rowData: ExtendedSitecoreContextType | SitecoreContextMappingType,
) => {
  if ('isError' in rowData && rowData.isError) return;
  if ('isLoading' in rowData && rowData.isLoading) return;

  // If it's a scoped context, retrieve parent context id
  if (
    'isScopedContext' in rowData &&
    rowData.isScopedContext &&
    rowData.masterSitecoreContextId
  ) {
    history.push({
      pathname: getContextIdsDetailsRoute(rowData.masterSitecoreContextId),
      search: history.location.search,
    });
  } else {
    // Non scoped context
    history.push({
      pathname: getContextIdsDetailsRoute(rowData.sitecoreContextId),
      search: history.location.search,
    });
  }
};

type ResourceOption = {
  value: string;
  label: string;
};

// All possible filter options (computed once — dynamic subset is derived per-org inside the component)
const ALL_RESOURCE_OPTIONS: ResourceOption[] = [
  ...Object.entries(RESOURCES_CONFIG)
    .filter(([key]) => key !== 'edge' && key !== 'discover') // Remove generic edge; 'discover' is legacy alias for 'search'
    .map(([key, config]) => ({
      value: key,
      label: config.label,
    })),
  // Add specific Edge variants
  { value: 'edge-live', label: 'Edge (Live)' },
  { value: 'edge-preview', label: 'Edge (Preview)' },
].sort((a, b) => a.label.localeCompare(b.label));

const formatResourceDisplay = (selectedResources: ResourceOption[]): string => {
  if (selectedResources.length === 0) return '';

  if (selectedResources.length <= 2) {
    // Show all items when ≤2 selections: "CDP, Search"
    return selectedResources.map((item) => item.label).join(', ');
  } else {
    // Show first 2 items + count when >2 selections: "CDP, Search +1"
    const displayedItems = selectedResources
      .slice(0, 2)
      .map((item) => item.label)
      .join(', ');
    const remainingCount = selectedResources.length - 2;
    return `${displayedItems} +${remainingCount}`;
  }
};

const CustomOption = (
  props: OptionProps<ResourceOption, true, GroupBase<ResourceOption>>,
) => {
  const { children, ...rest } = props;
  const isSensitive = isSensitiveResource(props.data.label);

  return (
    <Box
      px={4}
      py={2}
      bg={props.isSelected ? 'gray.200' : props.isFocused ? '#f7fafc' : 'white'}
      color={props.isSelected ? '#000000' : '#000000'}
      cursor='pointer'
      _hover={{
        bg: props.isSelected ? 'gray.200' : '#f7fafc',
      }}
      {...props.innerProps}
    >
      <Flex alignItems='center' justifyContent='space-between' width='100%'>
        <Text>{children}</Text>
        {isSensitive && (
          <SensitiveResourceIcon size='sm' iconSize='16px' isDecorative />
        )}
      </Flex>
    </Box>
  );
};

// Custom MultiValue component - only renders on first item to show consolidated display
const CustomMultiValue = (
  props: MultiValueProps<ResourceOption, true, GroupBase<ResourceOption>>,
) => {
  const { selectProps } = props;
  const allSelectedValues = selectProps.value as ResourceOption[];
  const isFirstItem =
    allSelectedValues.findIndex((val) => val.value === props.data.value) === 0;

  // Only render consolidated display for the first selected item to avoid duplicates
  // (React-Select calls this component once per selected item, we only want one display)
  if (!isFirstItem) {
    return null;
  }

  const resourceText = formatResourceDisplay(allSelectedValues);

  return (
    <Box
      px={0}
      py={0}
      borderRadius='md'
      fontSize='sm'
      mr={1}
      display='inline-flex'
      alignItems='center'
      color='var(--colors-primary-fg)'
    >
      <Text cursor='default' userSelect='none'>
        <Text as='span' fontWeight='600'>
          {CONTEXT_IDS_TEXT.RESOURCE_LABEL}
        </Text>
        {resourceText}
      </Text>
    </Box>
  );
};

export const ContextIdsTable = ({
  contexts = EMPTY_CONTEXTS,
}: {
  contexts?: SitecoreContextType[];
}) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchFilter, setSearchFilter] = useState('');
  const [searchStringIsValid, setSearchStringIsValid] = useState(true);
  const [selectedResources, setSelectedResources] = useState<ResourceOption[]>(
    [],
  );
  const initialSortBy = useMemo<DataTableSortingRule[]>(
    () => [{ id: 'updatedAt', desc: true }],
    [],
  );
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [loadedScopedContexts, setLoadedScopedContexts] = useState<
    Map<string, ExtendedSitecoreContextType[]>
  >(new Map());
  const [errorScopedContexts, setErrorScopedContexts] = useState<
    Map<string, Error>
  >(new Map());
  const [refetchFunctions, setRefetchFunctions] = useState<
    Map<string, () => void>
  >(new Map());
  const [selectedContextId, setSelectedContextId] = useState<string | null>(
    null,
  );
  const { applicationsNodes } = useGetApplicationNodes();

  const debouncedSearchFilter = useDebounce(searchFilter);
  const serverSearchTerm = searchStringIsValid
    ? debouncedSearchFilter.trim()
    : '';

  // Load More (cursor) pagination: the API only returns a `next` token, no total count,
  // so pages are appended to an accumulated list rather than fetched independently.
  const [continuationToken, setContinuationToken] = useState('');
  const [accumulatedContexts, setAccumulatedContexts] = useState<
    SitecoreContextType[]
  >([]);
  const [hasMoreData, setHasMoreData] = useState(false);

  // Reset the accumulated list whenever the search term or resource filter changes.
  useEffect(() => {
    setContinuationToken('');
    setAccumulatedContexts([]);
    setHasMoreData(false);
  }, [serverSearchTerm, selectedResources]);

  const handleListSuccess = useCallback(
    (data: ListSitecoreContextsResponse) => {
      if (data.data.length) {
        setAccumulatedContexts((curr) => [...curr, ...data.data]);
      }
      setHasMoreData(!!data.next);
    },
    [],
  );

  const handleListError = useCallback(() => {
    toast({
      title: 'Error',
      description: 'Failed to load context IDs. Please try again.',
      status: 'error',
      duration: 5000,
      isClosable: false,
    });
  }, [toast]);

  const { data: contextsResponse, isLoading: sitecoreContextsLoading } =
    useGetListSitecoreContexts(
      { search: serverSearchTerm, continuationToken, pageSize: PAGE_SIZE },
      { onSuccess: handleListSuccess, onError: handleListError },
    );

  const fetchedContexts =
    accumulatedContexts.length > 0
      ? accumulatedContexts
      : contextsResponse?.data;
  const showTableSkeleton =
    sitecoreContextsLoading && accumulatedContexts.length === 0;
  const showLoadMoreLoading =
    sitecoreContextsLoading && accumulatedContexts.length > 0;

  const onLoadMore = useCallback(() => {
    setContinuationToken(contextsResponse?.next ?? '');
  }, [contextsResponse]);

  const rawTableData = useMemo(
    () => fetchedContexts || contexts,
    [fetchedContexts, contexts],
  );

  // Only show resource types that actually exist in this org's contexts.
  // Edge is split into Live/Preview variants; 'search'/'discover' collapse to 'search'.
  const resourceOptions = useMemo<ResourceOption[]>(() => {
    const present = new Set<string>();
    (rawTableData as SitecoreContextType[]).forEach((context) => {
      context.resources?.forEach((resource) => {
        if (!isDisplayableResource(resource)) return;
        const resolvedResourceKey = (
          resource.tenantProductCode ??
          resource.type ??
          ''
        ).toLowerCase();
        if (resource.type === 'edge') {
          const edgeVariantFilterKey = getEdgeVariantFilterKey(resource);
          if (edgeVariantFilterKey) {
            present.add(edgeVariantFilterKey);
          }
        } else if (isSearchProductCode(resolvedResourceKey)) {
          present.add('search');
        } else if (resolvedResourceKey) {
          present.add(resolvedResourceKey);
        }
      });
    });
    return ALL_RESOURCE_OPTIONS.filter((opt) => present.has(opt.value));
  }, [rawTableData]);

  const tableDataMap = useMemo(() => {
    const map = new Map<string, SitecoreContextType>();
    const safeTableData = Array.isArray(rawTableData) ? rawTableData : [];
    safeTableData.forEach((context) => {
      if (context.sitecoreContextId) {
        map.set(context.sitecoreContextId, context);
      }
    });
    return map;
  }, [rawTableData]);

  const searchableContextTextById = useMemo(() => {
    const searchableMap = new Map<string, string>();
    const safeTableData = Array.isArray(rawTableData) ? rawTableData : [];
    safeTableData.forEach((context) => {
      const searchableText = [
        context.sitecoreContextId ?? '',
        context.name ?? '',
        context.description ?? '',
      ]
        .join(' ')
        .toLowerCase();
      searchableMap.set(context.sitecoreContextId, searchableText);
    });
    return searchableMap;
  }, [rawTableData]);

  const searchableScopedTextByParentId = useMemo(() => {
    const searchableMap = new Map<string, string>();
    loadedScopedContexts.forEach((scopedContexts, parentContextId) => {
      const searchableText = scopedContexts
        .map((context) =>
          [
            context.sitecoreContextId ?? '',
            context.name ?? '',
            context.description ?? '',
          ]
            .join(' ')
            .toLowerCase(),
        )
        .join(' ');
      searchableMap.set(parentContextId, searchableText);
    });
    return searchableMap;
  }, [loadedScopedContexts]);

  const resourceFilterKeysByContextId = useMemo(() => {
    const keysByContextId = new Map<string, Set<string>>();
    const safeTableData = Array.isArray(rawTableData) ? rawTableData : [];

    safeTableData.forEach((context) => {
      const resourceKeys = new Set<string>();
      context.resources?.forEach((resource) => {
        if (!isDisplayableResource(resource)) {
          return;
        }

        const resolvedResourceKey = (
          resource.tenantProductCode ??
          resource.type ??
          ''
        ).toLowerCase();

        if (resource.type === 'edge') {
          const edgeVariantFilterKey = getEdgeVariantFilterKey(resource);
          if (edgeVariantFilterKey) {
            resourceKeys.add(edgeVariantFilterKey);
          }
          return;
        }

        if (isSearchProductCode(resolvedResourceKey)) {
          resourceKeys.add('search');
          return;
        }

        if (resolvedResourceKey) {
          resourceKeys.add(resolvedResourceKey);
        }
      });

      keysByContextId.set(context.sitecoreContextId, resourceKeys);
    });

    return keysByContextId;
  }, [rawTableData]);

  const tableData = useMemo(() => {
    let filteredData: ExtendedSitecoreContextType[] =
      rawTableData as ExtendedSitecoreContextType[];
    const normalizedSearchFilter = debouncedSearchFilter.trim().toLowerCase();
    const hasActiveSearch = !!normalizedSearchFilter && searchStringIsValid;
    const selectedResourceValues = selectedResources.map(
      (resource) => resource.value,
    );

    // Filter by search text
    if (hasActiveSearch) {
      const filtered: ExtendedSitecoreContextType[] = [];
      filteredData.forEach((context) => {
        const contextSearchText =
          searchableContextTextById.get(context.sitecoreContextId) ?? '';
        const contextMatches = contextSearchText.includes(
          normalizedSearchFilter,
        );
        if (contextMatches) {
          filtered.push(context);
        } else {
          // Check scoped contexts if parent has no match
          const scopedSearchText =
            searchableScopedTextByParentId.get(context.sitecoreContextId) ?? '';
          const scopedMatches = scopedSearchText.includes(
            normalizedSearchFilter,
          );
          if (scopedMatches) {
            filtered.push(context);
          }
        }
      });
      filteredData = filtered;
    }

    // Filter by selected resources
    if (selectedResourceValues.length > 0) {
      filteredData = filteredData.filter((context) => {
        const contextResourceKeys = resourceFilterKeysByContextId.get(
          context.sitecoreContextId,
        );

        if (!contextResourceKeys || contextResourceKeys.size === 0) {
          return false;
        }

        return selectedResourceValues.some((value) =>
          contextResourceKeys.has(value),
        );
      });
    }

    return filteredData;
  }, [
    rawTableData,
    debouncedSearchFilter,
    searchStringIsValid,
    selectedResources,
    searchableContextTextById,
    searchableScopedTextByParentId,
    resourceFilterKeysByContextId,
  ]);

  const getSubRows = useCallback(
    (originalRow: any) => {
      const contextId = originalRow.sitecoreContextId;
      const scopedContexts = loadedScopedContexts.get(contextId);
      const error = errorScopedContexts.get(contextId);
      const hasLoadedData = loadedScopedContexts.has(contextId);
      const isLoading = expandedRows.has(contextId) && !hasLoadedData && !error;

      if (hasLoadedData && scopedContexts) {
        return scopedContexts;
      }

      // Only show error if we don't have loaded data (initial load error)
      if (error && !hasLoadedData) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        return [
          {
            isError: true,
            contextId,
            errorMessage,
            sitecoreContextId: `error-${contextId}`, // Unique ID for error row
            name: '',
            description: '',
            type: 'error' as const,
            resources: [],
            isScopedContext: false,
          },
        ];
      }

      if (isLoading) {
        const parentContext = originalRow as ExtendedSitecoreContextType;
        const filteredContextCount =
          (parentContext.filteredContexts?.length ?? 0) || 1;
        return Array.from({ length: filteredContextCount }, (_, index) => ({
          isLoading: true,
          sitecoreContextId: `skeleton-${contextId}-${index}`,
          name: '',
          description: '',
          type: 'loading' as const,
          resources: [],
          isScopedContext: false,
        }));
      }

      return [];
    },
    [loadedScopedContexts, errorScopedContexts, expandedRows],
  );

  const getCanExpand = useCallback((row: any) => {
    const context = row.original as ExtendedSitecoreContextType;
    // Can expand if it has filteredContext array with items and is not a scoped context
    return (
      !context.isScopedContext && (context.filteredContexts?.length ?? 0) > 0
    );
  }, []);

  const renderExpandIcon = useCallback((row: any) => {
    const context = row.original as ExtendedSitecoreContextType;
    const hasFilteredContext = (context.filteredContexts?.length ?? 0) > 0;

    // Only show expand icon for parent contexts with scoped contexts
    if (!hasFilteredContext || context.isScopedContext) {
      return null;
    }

    const contextId = context.sitecoreContextId;

    return (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          const willBeExpanded = !row.isExpanded;
          row.toggleRowExpanded();

          if (willBeExpanded) {
            setExpandedRows((prev) => new Set(prev).add(contextId));
          } else {
            setExpandedRows((prev) => {
              const newSet = new Set(prev);
              newSet.delete(contextId);
              return newSet;
            });
          }
        }}
        size='sm'
        variant='ghost'
        icon={
          <PortalIcon path={row.isExpanded ? mdiChevronUp : mdiChevronDown} />
        }
        aria-expanded={row.isExpanded ? 'true' : 'false'}
        aria-label={row.isExpanded ? 'Collapse row' : 'Expand row'}
      />
    );
  }, []);

  const handleRetryScopedContext = useCallback(
    (contextId: string) => {
      // Clear error state
      setErrorScopedContexts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(contextId);
        return newMap;
      });

      // Trigger refetch using the stored refetch function
      const refetch = refetchFunctions.get(contextId);
      if (refetch) {
        refetch();
      }
    },
    [refetchFunctions],
  );

  const handleRegenerate = useCallback(
    async (sitecoreContextId: string) => {
      const context = tableDataMap.get(sitecoreContextId);

      if (!context || context.resources.length === 0) {
        toast({
          title: 'Regeneration Error',
          description:
            'Cannot regenerate Context ID as there are no associated resources.',
          status: 'error',
          duration: 5000,
          isClosable: false,
        });
        return;
      }
      setSelectedContextId(sitecoreContextId);
      onOpen();
    },
    [onOpen, tableDataMap, toast],
  );

  const options = useMemo(
    () => getTableOptions(handleRegenerate, handleRetryScopedContext),
    [handleRegenerate, handleRetryScopedContext],
  );

  const onSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const searchInputValue = event.target.value;

    if (allowedSearchCharactersRegex.test(searchInputValue)) {
      setSearchFilter(searchInputValue);
      setSearchStringIsValid(true);
    } else {
      setSearchStringIsValid(false);
    }
  }, []);

  const onClearHandler = useCallback(() => {
    setSearchFilter('');
    setSearchStringIsValid(true);
  }, []);

  const hasActiveFilters =
    (searchFilter && searchStringIsValid) || selectedResources.length > 0;
  const hasNoData = !showTableSkeleton && tableData.length === 0;
  const showEmptySearchResults = hasNoData && hasActiveFilters;
  const showNoContextIds = hasNoData && !hasActiveFilters;

  const handleCloseModal = useCallback(() => {
    setSelectedContextId(null);
    onClose();
  }, [onClose, setSelectedContextId]);

  return (
    <Flex direction='column' width='100%' flex={1}>
      <RegenerateContextIdModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        contextId={selectedContextId}
        applicationsNodes={applicationsNodes}
      />
      <Flex gap={4} marginBottom={6} align='flex-start' wrap='wrap'>
        <FormControl isInvalid={!searchStringIsValid} maxW='xs'>
          <InputGroup mb='4'>
            <InputLeftElement
              pointerEvents='none'
              children={<PortalIcon path={mdiMagnify} />}
            />
            <Input
              placeholder={CONTEXT_IDS_TEXT.SEARCH_PLACEHOLDER}
              aria-label='Search context IDs'
              data-testid='context_ids_search_input'
              data-behavior-analytics-feature='context ids search'
              onChange={onSearch}
              value={searchFilter}
              role='search'
            />
            {searchFilter.length > 0 && (
              <InputRightElement>
                <Tooltip label={CONTEXT_IDS_TEXT.CLEAR_SEARCH}>
                  <CloseButton
                    onClick={onClearHandler}
                    data-testid='context_ids_search_clear_button'
                  />
                </Tooltip>
              </InputRightElement>
            )}
          </InputGroup>
          <FormErrorMessage>
            <FormErrorIcon />
            <T _str='Currently, the search is only available in alphanumeric characters, at signs (@), dots, dashes, and underscores.' />
          </FormErrorMessage>
        </FormControl>

        <Box minW='auto' maxW='400px'>
          <VisuallyHidden
            as='label'
            htmlFor='context_ids_resource_filter_input'
          >
            Filter by resources
          </VisuallyHidden>
          <ReactSelect<ResourceOption, true, GroupBase<ResourceOption>>
            inputId='context_ids_resource_filter_input'
            options={resourceOptions}
            value={selectedResources}
            onChange={(selectedOptions) => {
              setSelectedResources(selectedOptions as ResourceOption[]);
            }}
            placeholder={CONTEXT_IDS_TEXT.RESOURCE_FILTER_PLACEHOLDER}
            isClearable
            isDisabled={sitecoreContextsLoading}
            noOptionsMessage={() => CONTEXT_IDS_TEXT.NO_RESOURCES_AVAILABLE}
            loadingMessage={getLoadingMessage}
            components={{
              MultiValue: CustomMultiValue,
              Option: CustomOption,
            }}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            data-testid='context_ids_resource_filter'
            data-behavior-analytics-feature='context ids resource filter'
            menuPlacement='bottom'
            chakraStyles={{
              control: (provided, state) => {
                const hasSelection =
                  Array.isArray(state.selectProps.value) &&
                  state.selectProps.value.length > 0;
                return {
                  ...provided,
                  minHeight: '40px',
                  minWidth: '120px', // Always ensure minimum width for placeholder
                  width: hasSelection ? 'fit-content' : '120px',
                  backgroundColor: hasSelection
                    ? 'var(--colors-primary-bg)'
                    : provided.backgroundColor,
                  borderColor: hasSelection
                    ? 'var(--colors-primary-fg)'
                    : provided.borderColor,
                };
              },
              placeholder: (provided) => ({
                ...provided,
                whiteSpace: 'nowrap',
              }),
              clearIndicator: (provided) => ({
                ...provided,
                color: 'var(--colors-primary-fg)',
                '&:hover': {
                  color: 'var(--colors-primary-fg)',
                },
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                  ? 'gray.200'
                  : state.isFocused
                  ? '#f7fafc'
                  : provided.backgroundColor,
                color: state.isSelected
                  ? '#000000'
                  : state.isFocused
                  ? '#000000'
                  : provided.color,
                fontWeight: state.isSelected ? '600' : provided.fontWeight,
                '&:hover': {
                  backgroundColor: state.isSelected ? 'gray.200' : '#f7fafc',
                  color: state.isSelected ? '#000000' : provided.color,
                },
              }),
            }}
          />
        </Box>
      </Flex>

      {showEmptySearchResults ? (
        <Flex flex={1} justifyContent='center' alignItems='center'>
          <EmptyState
            text={t(CONTEXT_IDS_TEXT.NO_CONTEXT_IDS_FOUND)}
            dataTestId='no-context-search-results'
          />
        </Flex>
      ) : showNoContextIds ? (
        <Flex flex={1} justifyContent='center' alignItems='center'>
          <EmptyState
            text={CONTEXT_IDS_TEXT.NO_CONTEXT_IDS_CREATED}
            dataTestId='no-context-container'
          />
        </Flex>
      ) : (
        <Box overflowX='auto' width='100%'>
          <ScopedContextLoaders
            expandedRows={expandedRows}
            rawTableData={rawTableData}
            loadedScopedContexts={loadedScopedContexts}
            setErrorScopedContexts={setErrorScopedContexts}
            setLoadedScopedContexts={setLoadedScopedContexts}
            setExpandedRows={setExpandedRows}
            setRefetchFunctions={setRefetchFunctions}
          />
          <DataTable
            columns={options.columns}
            rowActions={options.rowActions}
            onChange={NOOP_TABLE_CHANGE}
            isLoading={showTableSkeleton}
            data={tableData}
            sortBy={initialSortBy}
            page={1}
            theaderProps={TABLE_HEADER_ANALYTICS_PROPS}
            onClickRow={onClickRow}
            pagination='hidden'
            getSubRows={getSubRows}
            getCanExpand={getCanExpand}
            renderExpandIcon={renderExpandIcon}
            expandedRowIds={expandedRows}
            data-behavior-analytics-feature='contexts table'
          />
        </Box>
      )}
      {((hasMoreData && !showTableSkeleton) || showLoadMoreLoading) && (
        <LoadMoreButton onClick={onLoadMore} isLoading={showLoadMoreLoading} />
      )}
    </Flex>
  );
};
